import React, { useEffect } from 'react';
import { useUpdateExternalAppForm } from '../../../hooks/useUpdateExternalAppForm';
import { Box, Button, Flexbox, Icon, Modal, TextField, Typography } from '@hiiretail/synergy-ui';
import { ExternalAppDto, UpdateExternalAppDto } from '../../../../../api-client';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { Controller } from 'react-hook-form';
import { UpdateExternalAppsMutationArgs } from '../../../hooks/useUpdateExternalApp';
import { ErrorResponse } from '../../../../../common/types/errorRespose';

type UpdateExternalAppModalProps = {
  onClose: () => void;
  isOpen: boolean;
  updateExternalApp: (data: UpdateExternalAppsMutationArgs) => void;
  isUpdating: boolean;
  externalApp: ExternalAppDto;
  error: ErrorResponse | null;
};

export const UpdateExternalAppModal: React.FC<UpdateExternalAppModalProps> = ({
  externalApp,
  onClose,
  isOpen,
  error,
  isUpdating,
  updateExternalApp,
}) => {
  const { control, handleSubmit, formState, reset } = useUpdateExternalAppForm();

  useEffect(() => {
    reset({
      name: externalApp.name,
      description: externalApp.description,
      apiKey: externalApp.apiKey,
    });
  }, [externalApp, reset]);

  const onFormSubmit = (data: UpdateExternalAppDto) => {
    updateExternalApp({
      id: externalApp.id,
      updateExternalAppDto: data,
    });
  };

  return (
    <Modal
      data-testid="update-external-app-modal"
      title={`Update ${externalApp.name}`}
      onClose={onClose}
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            type="submit"
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 1 ||
              isUpdating
            }
            onClick={handleSubmit(onFormSubmit)}
          >
            {isUpdating ? 'Updating ...' : 'Update'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <Flexbox container gutter={4} alignItems="flex-start">
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container>
          <Typography variant="body2">
            <strong>External Apps</strong> are specific named entity that are developed to create
            tenant apps. It provides a unique API KEYs functionality to exchange id tokens from
            tenant federated system for Hii Retail IAM tokens.
          </Typography>
        </Flexbox>
      </Flexbox>

      <form>
        <Box mb={2}>
          <Controller
            name="name"
            defaultValue={externalApp.name}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                required
                label="Name"
                name="name"
                type="text"
                ref={ref}
                // @ts-ignore
                hint={
                  <span>
                    Unique name of the external application
                    <br />
                    Must be between 3 and 256 characters long
                  </span>
                }
                value={value}
                onChange={onChange}
                error={formState.errors?.name?.message}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="description"
            defaultValue={externalApp.description}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Description"
                name="description"
                type="text"
                // @ts-ignore
                hint={
                  <span>
                    Describes your app's most notable characteristics
                    <br />
                    Must be between 5 and 256 characters long
                  </span>
                }
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.description?.message}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="apiKey"
            defaultValue={externalApp.apiKey}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="API Key"
                name="apiKey"
                required
                // @ts-ignore
                hint={
                  <span>
                    Key that will give you a possibility to exchange federated token on IAM token
                    <br />
                    Must be between 40 and 60 characters long
                  </span>
                }
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.apiKey?.message}
              />
            )}
          />
        </Box>
      </form>
    </Modal>
  );
};
